import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Navigate, useLocation } from "react-router-dom";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "nex_token";
export const PREVIEW_KEY = "nex_preview_token";  // tab-only (sessionStorage): a super admin's read-only "view as" session
export const getToken = () => sessionStorage.getItem(PREVIEW_KEY) || localStorage.getItem(TOKEN_KEY);

// Axios instance with token auto-attach
export const apiClient = axios.create({ baseURL: API });
apiClient.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const AuthCtx = createContext({ user: null, loading: true, login: async () => {}, logout: () => {} });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const check = useCallback(async () => {
    const t = getToken();
    if (!t) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
    } catch {
      if (sessionStorage.getItem(PREVIEW_KEY)) sessionStorage.removeItem(PREVIEW_KEY);
      else localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL (Emergent Google Auth playbook): if returning from OAuth callback,
    // skip the /auth/me check so AuthCallback can exchange the session_id first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    check();
  }, [check]);

  const login = async (email, password) => {
    const { data } = await apiClient.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const pinLogin = async (email, pin) => {
    const { data } = await apiClient.post("/auth/pin/login", { email, pin });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  // Exchange an Emergent Google session_id for a NEX JWT (allowlisted admins only).
  const googleExchange = async (sessionId) => {
    const { data } = await apiClient.post(
      "/auth/google/exchange",
      null,
      { headers: { "X-Session-ID": sessionId } }
    );
    if (data.kind === "resident") { localStorage.setItem("tea_resident_token", data.token); return { kind: "resident", ...data.resident }; }
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const applySession = (token, u) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser(u);
  };

  const logout = async () => {
    if (sessionStorage.getItem(PREVIEW_KEY)) { if (user?.login_as_by) await exitLoginAs(sessionStorage.getItem(PREVIEW_KEY)); exitPreview(); return; }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  return <AuthCtx.Provider value={{ user, loading, login, pinLogin, googleExchange, logout, applySession }}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);

/** Ends a super admin's Login As session on the server, using that session's own token (never the desk interceptor's). */
export const exitLoginAs = async (token) => {
  if (!token) return;
  try { await axios.post(`${API}/access/login-as/exit`, null, { headers: { Authorization: `Bearer ${token}` } }); } catch { /* the tab closes either way */ }
};

/** Leave a "view as" preview: this tab closes (or falls back to your own desktop). Your real session is untouched. */
export const exitPreview = () => {
  sessionStorage.removeItem(PREVIEW_KEY);
  window.close();
  setTimeout(() => { window.location.href = "/admin"; }, 150);
};

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F19]">
        <div className="mono-body text-[11px] uppercase tracking-[0.3em] text-nex-gold animate-pulse-gold">Authenticating...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" state={{ from: location }} replace />;
  return children;
};

export const formatApiError = (err) => {
  const raw = err?.response?.data?.detail;
  if (Array.isArray(raw)) return raw.map((e) => e.msg || JSON.stringify(e)).join(" · ");
  return String(raw || err?.message || "Something went wrong");
};
